import React, { useEffect, useState, useRef, useMemo, lazy, Suspense } from 'react';
import { useI18n } from '../i18n';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
// ChatBox is large; lazy-load to reduce initial bundle
const ChatBox = React.lazy(() => import('../components/ChatBox'));
import { Line, Bar, Chart as ReactChart } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { CandlestickController, CandlestickElement } from 'chartjs-chart-financial';

// Register required Chart.js components (including time scale)
Chart.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, BarElement, Tooltip, Legend, CandlestickController, CandlestickElement);

// Strategy metadata for grouping by Term and Cap (module-level so other parts can access it)
const STRATEGY_META = {
  ema_pullback: { title: 'EMA Pullback', term: 'Short → Long', cap: { large: 'excellent', small: 'warning' } },
  vwap_bounce: { title: 'VWAP Bounce', term: 'Short', cap: { large: 'excellent', small: 'bad' } },
  vwap_mean_reversion: { title: 'VWAP Mean Reversion', term: 'Short → Mid', cap: { large: 'excellent', small: 'bad' } },
  trend_day: { title: 'Trend Day', term: 'Mid → Long', cap: { large: 'good', small: 'good' } },
  orb: { title: 'ORB', term: 'Short', cap: { large: 'good', small: 'warning' } },
  gap_go: { title: 'Gap Go', term: 'Short → Mid', cap: { large: 'warning', small: 'excellent' } },
  bollinger_reversion: { title: 'Bollinger Reversion', term: 'Mid', cap: { large: 'good', small: 'bad' } },
  rsi_fade: { title: 'RSI Fade', term: 'Short → Mid', cap: { large: 'good', small: 'bad' } },
  support_resistance: { title: 'Support Resistance', term: 'All', cap: { large: 'good', small: 'warning' } },
  inside_bar: { title: 'Inside Bar', term: 'Short → Mid', cap: { large: 'good', small: 'warning' } },
  minute_momentum_scalp: { title: 'Minute Momentum Scalp', term: 'Short', cap: { large: 'good', small: 'warning' } },
  spread_scalping: { title: 'Spread Scalping', term: 'Short', cap: { large: 'good', small: 'bad' } },
  opening_drive: { title: 'Opening Drive', term: 'Short → Mid', cap: { large: 'good', small: 'good' } },
  pair_trading: { title: 'Pair Trading', term: 'Mid → Long', cap: { large: 'excellent', small: 'bad' } },
  momentum_ignition: { title: 'Momentum Ignition', term: 'Short', cap: { large: 'warning', small: 'excellent' } }
};

// Render a small icon/label for strategy cap values (ex: excellent, good, warning, bad)
function renderCapIcon(val) {
  if(!val) return <span style={{color:'var(--text-secondary)'}}>—</span>;
  switch(val){
    case 'excellent': return <span style={{color:'#10b981', fontWeight:700}}>✅ Excellent</span>;
    case 'good': return <span style={{color:'#10b981'}}>✓</span>;
    case 'warning': return <span style={{color:'#f59e0b'}}>⚠️</span>;
    case 'bad': return <span style={{color:'#ef4444'}}>❌</span>;
    default: return <span>{val}</span>;
  }
}

// Helper: normalize news item which may contain Python-repr or nested content
const normalizeNewsItem = (n) => {
  try{
    // If item is a bare string, try parse
    if(typeof n === 'string'){
      try{ const parsed = JSON.parse(n); if(parsed) n = parsed; }catch(e){
        // try converting single quotes to double quotes (best-effort)
        try{ const p2 = JSON.parse(n.replace(/\'/g, '"')); if(p2) n = p2; }catch(_){ }
      }
    }

    // prefer nested content.title if present
    let title = null;
    let summary = null;
    let url = null;

    if(n && typeof n === 'object'){
      // common paths
      title = n.title || n.headline || (n.content && (n.content.title || n.content.headline)) || null;
      summary = n.summary || n.description || (n.content && (n.content.summary || n.content.description)) || null;
      url = (n.clickThroughUrl && n.clickThroughUrl.url) || (n.canonicalUrl && n.canonicalUrl.url) || n.link || n.url || null;

      // If title still looks like a Python dict string, attempt regex extraction
      if(typeof title === 'string' && /\{\s*'?id'?\s*:/i.test(title)){
        // try extract 'title': '...'
        const m = title.match(/(?:'title'|"title")\s*:\s*['\"]([^'\"]{3,200})['\"]/i);
        if(m && m[1]) title = m[1];
        else {
          // try 'content':{...,'title': '...'} pattern
          const m2 = title.match(/'content'\s*:\s*\{[^}]*'(?:title)'\s*:\s*['\"]([^'\"]{3,200})['\"]/i);
          if(m2 && m2[1]) title = m2[1];
        }
      }
    }

    // Fallbacks
    if(!title) title = String(n || 'Untitled');
    if(!summary) summary = '';
    return { title: String(title), summary: String(summary), url };
  }catch(e){
    return { title: String(n || 'Untitled'), summary: '', url: null };
  }
};

const NewsList = lazy(() => import('../components/NewsList'));
/**
 * LoadingSpinner component - Displays a loading spinner
 * @param {Object} props - Component props
 * @param {number} props.size - Size of the spinner in pixels
 * @param {string} props.color - Color of the spinner
 * @returns {JSX.Element} Loading spinner element
 */
function LoadingSpinner({ size = 20, color = 'var(--accent-blue)' }) {
  return (
    <div style={{
      width: size,
      height: size,
      border: `2px solid ${color}20`,
      borderTop: `2px solid ${color}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
      marginRight: 8
    }} />
  );
}

/**
 * Formats a number with short notation (K, M, B, T)
 * @param {number} v - Value to format
 * @returns {string} Formatted number string
 */
function fmtShortNumber(v){
  if(v === null || v === undefined) return '—';
  const n = Number(v);
  if(isNaN(n)) return String(v);
  const abs = Math.abs(n);
  if(abs >= 1e12) return (n/1e12).toFixed(2) + 'T';
  if(abs >= 1e9) return (n/1e9).toFixed(2) + 'B';
  if(abs >= 1e6) return (n/1e6).toFixed(2) + 'M';
  return n.toString();
}

/**
 * Formats a currency value with appropriate symbol and precision
 * @param {number} v - Value to format
 * @param {string} currency - Currency code (optional, defaults to USD)
 * @returns {string} Formatted currency string
 */
function fmtCurrency(v, currency = 'USD') {
  if(v === null || v === undefined) return '—';
  const n = Number(v);
  if(isNaN(n)) return String(v);

  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'INR': '₹',
    'VND': '₫'
  };


  const symbol = currencySymbols[currency] || '$';

  // For very small values, show more decimal places
  if(Math.abs(n) < 0.01) {
    return symbol + n.toFixed(6);
  }
  // For small values, show 4 decimal places
  else if(Math.abs(n) < 1) {
    return symbol + n.toFixed(4);
  }
  // For normal values, show 2 decimal places
  else {
    return symbol + n.toFixed(2);
  }
}

/**
 * KeyMetrics component for displaying stock metrics
 * @param {Object} props - Component props
 * @param {Object} props.data - Stock data object
 * @param {Object} props.indicators - Technical indicators
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Key metrics display
 */
function KeyMetrics({ data, indicators, loading, t, currency = 'USD' }){
  const tt = t && typeof t === 'function' ? t : (k => k);
  const [expanded, setExpanded] = useState(false);
  const [showExplanation, setShowExplanation] = useState(null);

  const mt = [
    { k: 'P/E', v: data?.peRatio ?? '—', explanation: 'Price-to-Earnings ratio. Current stock price divided by earnings per share. Lower values may indicate undervaluation.' },
    { k: 'EPS', v: data?.eps ?? '—', explanation: 'Earnings Per Share. Company\'s profit divided by outstanding shares. Higher values indicate better profitability.' },
    { k: 'Avg Volume', v: data?.avgVolume ? fmtShortNumber(data.avgVolume) : '—', explanation: 'Average daily trading volume over the past 30 days. Higher volume indicates better liquidity.' },
    { k: 'RSI', v: indicators?.rsi ? Number(indicators.rsi).toFixed(2) : '—', explanation: 'Relative Strength Index. Momentum oscillator measuring price changes. Above 70 = overbought, below 30 = oversold.' },
    { k: 'SMA 20', v: indicators?.sma_20 ? fmtCurrency(indicators.sma_20, currency) : '—', explanation: 'Simple Moving Average over 20 periods. Shows average price trend over short-term.' },
    { k: 'SMA 50', v: indicators?.sma_50 ? fmtCurrency(indicators.sma_50, currency) : '—', explanation: 'Simple Moving Average over 50 periods. Shows average price trend over medium-term.' },
    { k: 'EMA 12', v: indicators?.ema_12 ? fmtCurrency(indicators.ema_12, currency) : '—', explanation: 'Exponential Moving Average over 12 periods. Gives more weight to recent prices.' },
    { k: 'EMA 26', v: indicators?.ema_26 ? fmtCurrency(indicators.ema_26, currency) : '—', explanation: 'Exponential Moving Average over 26 periods. Used in MACD calculations.' },
    { k: 'BB Upper', v: indicators?.bb_upper ? fmtCurrency(indicators.bb_upper, currency) : '—', explanation: 'Bollinger Band upper line. 2 standard deviations above SMA. Shows resistance levels.' },
    { k: 'BB Lower', v: indicators?.bb_lower ? fmtCurrency(indicators.bb_lower, currency) : '—', explanation: 'Bollinger Band lower line. 2 standard deviations below SMA. Shows support levels.' },
    { k: 'BB Width', v: indicators?.bb_width ? Number(indicators.bb_width).toFixed(2) + '%' : '—', explanation: 'Bollinger Band width percentage. Shows volatility - wider bands = higher volatility.' },
    { k: 'MACD', v: indicators?.macd ? Number(indicators.macd).toFixed(4) : '—', explanation: 'Moving Average Convergence Divergence. Difference between EMA 12 and EMA 26.' },
    { k: 'MACD Signal', v: indicators?.macd_signal ? Number(indicators.macd_signal).toFixed(4) : '—', explanation: 'MACD signal line. 9-period EMA of MACD line. Crossovers indicate trend changes.' },
    { k: 'MACD Diff', v: indicators?.macd_diff ? Number(indicators.macd_diff).toFixed(4) : '—', explanation: 'MACD histogram. Difference between MACD and signal line. Shows momentum.' },
    { k: 'ATR', v: indicators?.atr ? Number(indicators.atr).toFixed(2) : '—', explanation: 'Average True Range. Measures volatility by calculating average price range.' },
    { k: 'CCI', v: indicators?.cci ? Number(indicators.cci).toFixed(2) : '—', explanation: 'Commodity Channel Index. Identifies cyclical trends. Above 100 = overbought, below -100 = oversold.' },
    { k: 'Stochastic %K', v: indicators?.stoch_k ? Number(indicators.stoch_k).toFixed(2) : '—', explanation: 'Stochastic %K line. Compares closing price to price range over N periods.' },
    { k: 'Stochastic %D', v: indicators?.stoch_d ? Number(indicators.stoch_d).toFixed(2) : '—', explanation: 'Stochastic %D line. 3-period moving average of %K. Smoother signal line.' },
    { k: 'Williams %R', v: indicators?.williams_r ? Number(indicators.williams_r).toFixed(2) : '—', explanation: 'Williams %R. Momentum indicator similar to Stochastic. Above -20 = overbought, below -80 = oversold.' },
    { k: 'ROC', v: indicators?.roc ? Number(indicators.roc).toFixed(2) + '%' : '—', explanation: 'Rate of Change. Percentage change in price over specified period. Shows momentum.' },
    { k: 'MFI', v: indicators?.mfi ? Number(indicators.mfi).toFixed(2) : '—', explanation: 'Money Flow Index. Volume-weighted RSI. Above 80 = overbought, below 20 = oversold.' },
    { k: 'ADX', v: indicators?.adx ? Number(indicators.adx).toFixed(2) : '—', explanation: 'Average Directional Index. Measures trend strength. Above 25 = strong trend, below 20 = weak trend.' },
    { k: 'ADX +', v: indicators?.adx_pos ? Number(indicators.adx_pos).toFixed(2) : '—', explanation: 'Positive Directional Indicator (DI+). Higher values indicate stronger upward directional movement.' },
    { k: 'ADX -', v: indicators?.adx_neg ? Number(indicators.adx_neg).toFixed(2) : '—', explanation: 'Negative Directional Indicator (DI-). Higher values indicate stronger downward directional movement.' },
    { k: 'OBV', v: indicators?.obv ? fmtShortNumber(indicators.obv) : '—', explanation: 'On-Balance Volume. Cumulative volume measure that relates volume flow to price movement.' }
  ];

  // Show only first 6 items (3 rows) when collapsed
  const displayItems = expanded ? mt : mt.slice(0, 6);

  return (
    <div className="key-metrics" style={{flex:1, background:'var(--bg-tertiary)', padding:12, borderRadius:8}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
        <h4 style={{margin:0, color:'var(--text-secondary)'}}>{tt('keymetrics.title')}</h4>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid var(--border-color)',
            background: 'transparent',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: 12
          }}
        >
          {expanded ? 'Thu gọn' : 'Xem thêm'}
        </button>
      </div>
      <div className="metrics-grid" style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8}}>
        {loading ? Array.from({length:displayItems.length}).map((_,i)=>(
          <div key={i} className="metric-item" style={{background:'rgba(255,255,255,0.02)', padding:10, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center'}}>
            <LoadingSpinner size={16} />
            <span style={{fontSize:12, color:'var(--text-secondary)'}}>Loading...</span>
          </div>
        )) : displayItems.map((m)=> (
          <div key={m.k} className="metric-item" style={{background:'rgba(255,255,255,0.02)', padding:10, borderRadius:8, position:'relative'}}>
            <div style={{display:'flex', alignItems:'center', gap:4}}>
              <div style={{fontSize:12, color:'var(--text-secondary)'}}>{m.k}</div>
              <button
                onClick={() => setShowExplanation(showExplanation === m.k ? null : m.k)}
                style={{
                  width:12,
                  height:12,
                  borderRadius:'50%',
                  border:'1px solid var(--text-secondary)',
                  background:'transparent',
                  color:'var(--text-secondary)',
                  cursor:'pointer',
                  fontSize:10,
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center',
                  padding:0
                }}
                title="Click for explanation"
              >
                ?
              </button>
            </div>
            <div style={{fontWeight:700, marginTop:6}}>{m.v}</div>
            {showExplanation === m.k && (
              <div style={{
                position:'absolute',
                top:'100%',
                left:0,
                right:0,
                background:'var(--bg-primary)',
                border:'1px solid var(--border-color)',
                borderRadius:4,
                padding:8,
                marginTop:4,
                fontSize:11,
                color:'var(--text-secondary)',
                zIndex:10,
                boxShadow:'0 2px 8px rgba(0,0,0,0.3)'
              }}>
                {m.explanation}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * StockDetail component - Displays detailed information for a specific stock
 * @returns {JSX.Element} The stock detail page
 */
export default function StockDetail(){
  const { t, lang } = useI18n();
  const { symbol } = useParams();
  const [data, setData] = useState(null);
  const [historical, setHistorical] = useState(null);
  
  // Get currency from data
  const currency = data?.currency || 'USD';
  const chartRef = useRef(null);
  const panelRef = useRef(null);
  const [badgeTop, setBadgeTop] = useState(null);
  const badgeRef = useRef(null);
  const [connector, setConnector] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [displayPrice, setDisplayPrice] = useState(null);
  const [displayChangePct, setDisplayChangePct] = useState(null);
  const API_BASE = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';
  const navigate = useNavigate();

  const [selectedRange, setSelectedRange] = useState('1M');
  const [chartType, setChartType] = useState('line'); // options: line, mountain, bar
  const [showHistoricalBlock, setShowHistoricalBlock] = useState(true);
  const [followed, setFollowed] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiAutoRunDone, setAiAutoRunDone] = useState(false);
  const [aiTranslated, setAiTranslated] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiRef = useRef(null);
  const [daytradeLoading, setDaytradeLoading] = useState(false);
  const [daytradeResult, setDaytradeResult] = useState(null);
  const [savedRuns, setSavedRuns] = useState(null);
  const [daytradeSaving, setDaytradeSaving] = useState(false);
  const [daytradeSaveSuccess, setDaytradeSaveSuccess] = useState(null);
  const [daytradeSaveError, setDaytradeSaveError] = useState(null);
  const [daytradeAutoSave, setDaytradeAutoSave] = useState(false);
  const [indicators, setIndicators] = useState(null);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [backtestData, setBacktestData] = useState(null);
  const [loadingBacktest, setLoadingBacktest] = useState(false);
  const [strategiesData, setStrategiesData] = useState({});
  const [loadingStrategies, setLoadingStrategies] = useState({});
  const [strategyRatings, setStrategyRatings] = useState({});
  const [tradeSuggestionLoading, setTradeSuggestionLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [realTimeData, setRealTimeData] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const realTimeIntervalRef = useRef(null);
  const [strategyParams, setStrategyParams] = useState({
    ema_pullback: {ema_short: 9, ema_long: 20, sma_trend: 50, stop_loss_pct: 0.01, take_profit_mult: 2.0},
    vwap_bounce: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    trend_day: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    orb: {or_hours: 2, stop_loss_pct: 0.01, take_profit_mult: 2.0},
    momentum_ignition: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    gap_go: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    vwap_mean_reversion: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    bollinger_reversion: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    rsi_fade: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    support_resistance: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    inside_bar: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    minute_momentum_scalp: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    spread_scalping: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    opening_drive: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
    pair_trading: {stop_loss_pct: 0.01, take_profit_mult: 2.0}
    ,
    largecap_morning_afternoon: {market_symbol: 'SELF', entry_start: '09:35', entry_end: '11:00', risk_per_trade: 0.005, max_trades_per_day: 2}
  });
  const [showStrategyParams, setShowStrategyParams] = useState(false);
  const [showStrategyExplanations, setShowStrategyExplanations] = useState(false);
  const [showTradeFormulas, setShowTradeFormulas] = useState(false);
  const [showTradeParameters, setShowTradeParameters] = useState(false);
  const [expandedStrategies, setExpandedStrategies] = useState({});
  const [overlays, setOverlays] = useState({ ma5: false, ma20: false, ma50: false, ma200: false, rsi: false, bb: false, atr: false });
  const [activeSuggestionStrategy, setActiveSuggestionStrategy] = useState(null);
  const [goodTimeResult, setGoodTimeResult] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [loadingGoodTime, setLoadingGoodTime] = useState(false);

  // Backtest runner: posts local historical OHLCV to backend /api/backtest
  async function runBacktest(opts = {}){
    if(!historical || !historical.candles || !historical.candles.length){
      setToasts(ts => ([...ts, { type: 'error', text: 'No historical data available for backtest' }]));
      return;
    }
    setLoadingBacktest(true);
    setBacktestData(null);
    try{
      const ohlcv = historical.candles.map(c => ({ date: new Date(c.x).toISOString(), open: c.o, high: c.h, low: c.l, close: c.c, volume: c.v }));
      const payload = { ohlcv, params: opts, split: true };
      const res = await axios.post(`${API_BASE}/api/backtest`, payload, { timeout: 120000 });
      setBacktestData(res.data);
    }catch(e){
      console.error('Backtest error', e);
      setBacktestData(null);
      setToasts(ts => ([...ts, { type: 'error', text: 'Backtest failed (see console)' }]));
    }finally{
      setLoadingBacktest(false);
    }
  }

  // Backtest UI parameters
  const [backtestInitialEquity, setBacktestInitialEquity] = useState(100000);
  const [backtestRiskPct, setBacktestRiskPct] = useState(0.01); // stored as decimal (0.01 => 1%)
  const [marketBenchmark, setMarketBenchmark] = useState('SPY');
  const [showGoodTimePanel, setShowGoodTimePanel] = useState(false);

  // Shared map of strategy formulas for consistent display
  const TRADE_FORMULAS = {
    ema_pullback: { title: 'EMA Pullback', formula: 'Entry: Price ≤ EMA(9) ∧ Price ≥ EMA(20) ∧ Price ≥ SMA(200)\nExit: Price < EMA(20)', params: 'EMA Short: 9, EMA Long: 20, SMA Trend: 200' },
    vwap_bounce: { title: 'VWAP Bounce', formula: 'Entry: Price > VWAP ∧ Volume > Avg_Volume\nExit: Price < VWAP', params: 'Volume Threshold: Above Average' },
    trend_day: { title: 'Trend Day', formula: 'Entry: Higher_Highs ∧ Higher_Lows\nExit: Pattern Break', params: 'Pattern Recognition: Consecutive HH/HL' },
    orb: { title: 'ORB (Opening Range Breakout)', formula: 'Entry: Price > OR_High (first 2h)\nExit: Price < OR_Low', params: 'Opening Range Hours: 2' },
    momentum_ignition: { title: 'Momentum Ignition', formula: 'Entry: Volume_Surge ∧ Price > VWAP ∧ Price > EMA(20)\nExit: Price < VWAP', params: 'Volume Surge Threshold: Dynamic' },
    gap_go: { title: 'Gap & Go', formula: 'Entry: Price > Open_Price (Gap Up)\nExit: Price ≤ Open_Price', params: 'Gap Size: Any positive gap' },
    vwap_mean_reversion: { title: 'VWAP Mean Reversion', formula: 'Entry: Price ≤ VWAP * 0.98 ∧ RSI < 30\nExit: Price ≥ VWAP', params: 'Deviation: 2%, RSI Threshold: 30' },
    bollinger_reversion: { title: 'Bollinger Reversion', formula: 'Entry: Price ≤ Lower_BB ∧ RSI < 30\nExit: Price ≥ Middle_SMA', params: 'BB Period: 20, Deviation: 2, RSI: 30' },
    rsi_fade: { title: 'RSI Fade', formula: 'Entry: RSI < 30\nExit: RSI > 70', params: 'Oversold: 30, Overbought: 70' },
    support_resistance: { title: 'Support & Resistance', formula: 'Entry: Price > Resistance_Level\nExit: Price < Support_Level', params: 'Dynamic S/R detection' },
    inside_bar: { title: 'Inside Bar Breakout', formula: 'Entry: Price > Inside_Bar_High\nExit: Price < Inside_Bar_Low', params: 'Inside Bar Pattern Recognition' },
    minute_momentum_scalp: { title: 'Minute Momentum Scalp', formula: 'Entry: Volume_Surge ∧ Price > VWAP\nExit: Price < VWAP', params: 'Timeframe: 1 minute' },
    spread_scalping: { title: 'Spread Scalping', formula: 'Entry: Small_Move (<0.5%)\nExit: Price ±1% from Entry', params: 'Max Move: 0.5%, Target: 1%' },
    opening_drive: { title: 'Opening Drive', formula: 'Entry: Price_Reversal from Open ∧ Price < VWAP\nExit: Target or VWAP', params: 'Opening Drive Detection' },
    pair_trading: { title: 'Pair Trading', formula: 'Entry: Price < Lower_BB\nExit: Price > Upper_BB', params: 'BB Period: 20, Deviation: 2' }
  };

  // Check if current time is within trading hours (9:30 AM - 4:00 PM EST, Monday-Friday)
  function isTradingHours() {
    const now = new Date();
    const estOffset = now.getTimezoneOffset() + 240; // EST is UTC-4, EDT is UTC-5
    const estTime = new Date(now.getTime() + (estOffset * 60000));
    // Check if it's a weekday (Monday = 1, Friday = 5)
    const day = estTime.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    if (day < 1 || day > 5) return false;
    const hours = estTime.getHours();
    const minutes = estTime.getMinutes();
    const minutesOfDay = hours * 60 + minutes;
    const start = 9 * 60 + 30; // 09:30
    const end = 16 * 60; // 16:00
    return minutesOfDay >= start && minutesOfDay <= end;
  }

  // Run AI analysis: fetch insights, predictions and a quick daytrade backtest
  async function handleAnalyze(){
    if(!symbol) return;
    setAiLoading(true);
    setAiError(null);
    try{
      const cfg = rangeMap[selectedRange] || rangeMap['1M'];
      const insP = axios.get(`${API_BASE}/api/insights/${symbol}?period=${encodeURIComponent(cfg.period)}${lang === 'vi' ? '&translate=vi' : ''}`);
      const p1 = axios.get(`${API_BASE}/api/predict/${symbol}?days_ahead=1&period=${encodeURIComponent(cfg.period)}`);
      const p3 = axios.get(`${API_BASE}/api/predict/${symbol}?days_ahead=3&period=${encodeURIComponent(cfg.period)}`);
      const p7 = axios.get(`${API_BASE}/api/predict/${symbol}?days_ahead=7&period=${encodeURIComponent(cfg.period)}`);
      const dt = axios.get(`${API_BASE}/api/daytrade/${symbol}?interval=5m&period=7d&initial_balance=10000&risk_per_trade=0.01&max_trades_per_day=3`);
      setDaytradeLoading(true);
      const [insRes, r1, r3, r7, dtRes] = await Promise.all([insP, p1, p3, p7, dt]);
      // Normalize insights: backend may return string or list
      const rawInsights = insRes.data?.insights;
      if(Array.isArray(rawInsights)){
        setAiInsights(rawInsights);
      } else if(rawInsights){
        setAiInsights([String(rawInsights)]);
      } else {
        setAiInsights([]);
      }
      setAiTranslated(Boolean(insRes.data?.translated));
      // store full API responses per horizon — rendering will pick the correct day entry
      const preds = {
        1: r1?.data ?? null,
        3: r3?.data ?? null,
        7: r7?.data ?? null
      };
      console.debug('AI predictions received (raw)', preds);
      setPredictions(preds);
      // set daytrade result from backtest
      try{ setDaytradeResult(dtRes?.data ?? null); }catch(e){ setDaytradeResult({ error: 'No result' }) }
      setDaytradeLoading(false);
      setTimeout(()=>{ try{ aiRef.current?.scrollIntoView({behavior:'smooth', block:'start'}); }catch(e){} },100);
    }catch(e){
      // Prefer backend-provided error message when available
      let msg = 'Unable to fetch AI analysis. Vui lòng thử lại hoặc kiểm tra kết nối.';
      try{
        if(e && e.response && e.response.data){
          const d = e.response.data;
          msg = d.error || d.message || d.detail || (d.insights ? String(d.insights) : JSON.stringify(d));
        } else if(e && e.message){
          msg = e.message;
        }
      }catch(_){ }
      setAiError(msg);
    }finally{ setAiLoading(false); }
  }

  // Auto-run AI analysis once when the page loads or when symbol/indicators/history change
  useEffect(() => {
    if(!symbol) return;
    if(aiAutoRunDone) return;
    if(aiLoading) return;

    // Determine if we have sufficient data to run analysis:
    // - indicators present OR
    // - historical prices length >= 30 (and not currently loading history)
    const hasIndicators = indicators && Object.keys(indicators).length > 0;
    const hasHistory = historical && historical.prices && Array.isArray(historical.prices) && historical.prices.length >= 30 && !loadingHistory;

    if(hasIndicators || hasHistory){
      setAiAutoRunDone(true);
      // fire-and-forget
      handleAnalyze().catch(() => {});
    }
  }, [symbol, indicators, historical, loadingHistory, loadingIndicators, aiLoading, aiAutoRunDone]);

  async function runStrategy(strategyName, params = {}, explain = false, sample_count = 3){
    if(!symbol) return;
    setLoadingStrategies(prev => ({...prev, [strategyName]: true}));
    try{
      const paramsStr = JSON.stringify(params);
      const cfg = rangeMap[selectedRange] || rangeMap['1M'];
      // include market filter to improve regime-aware strategy checks
      const marketFilter = getMarketFilter(symbol);
      let url = `${API_BASE}/api/strategy/${strategyName}/${symbol}?params=${encodeURIComponent(paramsStr)}&period=${encodeURIComponent(cfg.period)}&market_filter=${encodeURIComponent(marketFilter)}`;
      if(explain){ url += `&explain=true&sample_count=${Number(sample_count) || 3}`; }
      const r = await axios.get(url);
      setStrategiesData(prev => ({...prev, [strategyName]: r.data}));
    }catch(e){
      setStrategiesData(prev => ({...prev, [strategyName]: {error: e?.response?.data?.error || 'Unable to run strategy'}}));
    }finally{
      setLoadingStrategies(prev => ({...prev, [strategyName]: false}));
    }
  }

  // Helper: determine a market filter symbol to use for regime/market comparisons
  function getMarketFilter(sym){
    // UI override first
    if(marketBenchmark) return marketBenchmark;
    if(!sym) return 'SPY';
    const s = (sym || '').toUpperCase();
    if(s.endsWith('.VN')) return 'VNINDEX';
    if(s.endsWith('.TO')) return 'TSX';
    // treat common US symbols as SPY benchmark
    return 'SPY';
  }

  async function runGoodTime(include_proof = true, sample_count = 5){
    if(!symbol) return;
    setLoadingGoodTime(true);
    try{
      const cfg = rangeMap[selectedRange] || rangeMap['1M'];
      const marketFilter = getMarketFilter(symbol);
      let url = `${API_BASE}/api/goodtime/${symbol}?period=${encodeURIComponent(cfg.period)}&market_filter=${encodeURIComponent(marketFilter)}`;
      if(include_proof) url += `&include_proof=true&sample_count=${Number(sample_count) || 5}`;
      const r = await axios.get(url);
      setGoodTimeResult(r.data);
      setShowGoodTimePanel(true);
    }catch(e){
      setGoodTimeResult({ error: e?.response?.data?.error || 'Unable to evaluate Good Time' });
      setShowGoodTimePanel(true);
    }finally{ setLoadingGoodTime(false); }
  }

  async function runTradeSuggestion(){
    if(!symbol) return;
    setTradeSuggestionLoading(true);
    setStrategyRatings({});

    const strategyNames = [
      'ema_pullback', 'vwap_bounce', 'trend_day', 'orb', 'momentum_ignition', 'gap_go',
      'vwap_mean_reversion', 'bollinger_reversion', 'rsi_fade', 'support_resistance',
      'inside_bar', 'minute_momentum_scalp', 'spread_scalping', 'opening_drive', 'pair_trading'
    ];

    const cfg = rangeMap[selectedRange] || rangeMap['1M'];
    const ratings = {};

    // Determine market filter for symbol once
    const marketFilterForSymbol = getMarketFilter(symbol);

    // Run all strategies sequentially
    for(const strategyName of strategyNames){
      try{
        setLoadingStrategies(prev => ({...prev, [strategyName]: true}));
        const paramsStr = JSON.stringify(strategyParams[strategyName] || {});
        const r = await axios.get(`${API_BASE}/api/strategy/${strategyName}/${symbol}?params=${encodeURIComponent(paramsStr)}&period=${encodeURIComponent(cfg.period)}&market_filter=${encodeURIComponent(marketFilterForSymbol)}`);
        
        setStrategiesData(prev => ({...prev, [strategyName]: r.data}));
        
        // Evaluate strategy performance
        if(r.data && !r.data.error && r.data.metrics){
          const metrics = r.data.metrics;
          const expectancy = metrics.expectancy || 0;
          const winRate = metrics.win_rate || 0;
          const totalTrades = metrics.total_trades || 0;
          
          // Rating logic: Good (Green), Neutral (Yellow), Bad (Red)
          let rating = 'neutral';
          if(totalTrades >= 5){ // Need minimum trades for reliable rating
            if(expectancy > 0.01 && winRate > 0.55){
              rating = 'good';
            }else if(expectancy < -0.01 || winRate < 0.45){
              rating = 'bad';
            }
          }
          
          ratings[strategyName] = rating;
        }else{
          ratings[strategyName] = 'neutral'; // Default for errors or insufficient data
        }
      }catch(e){
        setStrategiesData(prev => ({...prev, [strategyName]: {error: e?.response?.data?.error || 'Unable to run strategy'}}));
        ratings[strategyName] = 'neutral';
      }finally{
        setLoadingStrategies(prev => ({...prev, [strategyName]: false}));
      }
    }

    setStrategyRatings(ratings);
    setTradeSuggestionLoading(false);
    
    addToast('Trade suggestion analysis completed!', 'success');
  }

  function closeStrategy(strategyName){
    setStrategiesData(prev => {
      const newData = {...prev};
      delete newData[strategyName];
      return newData;
    });
  }

  function addToast(message, kind = 'info', ttl = 4000){
    const id = Date.now() + Math.random().toString(36).slice(2,8);
    const t = { id, message, kind };
    setToasts(s => [...s, t]);
    setTimeout(()=> setToasts(s => s.filter(x => x.id !== id)), ttl);
  }

  useEffect(()=>{
    // when a backtest finishes and auto-save is enabled, persist it
    if(daytradeAutoSave && daytradeResult && !daytradeResult.error){
      saveDaytradeRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daytradeResult, daytradeAutoSave]);

  // debug: log predictions when they update
  useEffect(()=>{
    try{ console.debug('predictions state updated', predictions); }catch(e){}
  }, [predictions]);

  const rangeMap = {
    '1D': { period: '1d', interval: '5m' },
    '1W': { period: '7d', interval: '30m' },
    '1M': { period: '1mo', interval: '1d' },
    '3M': { period: '3mo', interval: '1d' },
    '6M': { period: '6mo', interval: '1d' },
    'YTD': { period: 'ytd', interval: '1d' },
    '1Y': { period: '1y', interval: '1d' },
    '2Y': { period: '2y', interval: '1d' },
    '5Y': { period: '5y', interval: '1d' },
    'All': { period: 'max', interval: '1d' }
  };

  useEffect(()=>{
    if(!symbol) return;
    setError(null);
    const timeoutId = setTimeout(() => {
      setLoadingDetails(false);
      setLoadingIndicators(false);
    }, 10000); // Force stop loading after 10 seconds

    (async ()=>{
      setLoadingDetails(true);
      try{
        const r = await axios.get(`${API_BASE}/api/yahoo/details/${symbol}`);
        setData(r.data);
        // initialize display values from details
        setDisplayPrice(r.data?.price ?? null);
        setDisplayChangePct(r.data?.change_percent ?? null);
        // init follow state from localStorage
        try{
          const s = JSON.parse(window.localStorage.getItem('market360.follows') || '[]');
          setFollowed(Array.isArray(s) && s.includes(symbol));
        }catch(e){ setFollowed(false); }
      }catch(e){
        setError('details');
        setData(null);
      }finally{ 
        setLoadingDetails(false); 
        clearTimeout(timeoutId);
      }

      // Fetch indicators
      setLoadingIndicators(true);
      try{
        const ind = await axios.get(`${API_BASE}/api/analysis/${symbol}`);
        setIndicators(ind.data);
      }catch(e){
        setIndicators(null);
      }finally{ 
        setLoadingIndicators(false); 
        clearTimeout(timeoutId);
      }

      // Backtest will be triggered manually from the UI to avoid automatic heavy jobs
      setBacktestData(null);
    })();
  }, [symbol]);

  useEffect(()=>{
    if(!symbol) return;
    // fetch persisted suggestion for this symbol
    setLoadingSuggestion(true);
    axios.get(`${API_BASE}/api/suggestions/latest/${symbol}`)
      .then(r => setSuggestion(r.data))
      .catch(()=> setSuggestion(null))
      .finally(()=> setLoadingSuggestion(false));

    const cfg = rangeMap[selectedRange] || rangeMap['1M'];
    const qs = 'period=' + encodeURIComponent(cfg.period) + '&interval=' + encodeURIComponent(cfg.interval);
    setLoadingHistory(true);
    setHistorical(null);
    (async ()=>{
      try{
        const h = await axios.get(`${API_BASE}/api/yahoo/history/${symbol}?${qs}`);
        const recs = h.data?.history || [];
        const labels = recs.map(r=> {
          try{ return new Date(r.timestamp).toLocaleDateString(); }catch{ return String(r.timestamp) }
        });
        const prices = recs.map(r=> Number(r.close));
        const volumes = recs.map(r=> Number(r.volume || 0));
        const ohlc = recs.map(r=> ({ open: Number(r.open), high: Number(r.high), low: Number(r.low), close: Number(r.close) }));
        // candles: { x: timestamp(ms), o, h, l, c, v }
        const candles = recs.map(r=> ({ x: new Date(r.timestamp).getTime(), o: Number(r.open), h: Number(r.high), l: Number(r.low), c: Number(r.close), v: Number(r.volume || 0) }));
        // volume points for time scale
        const volumesXY = recs.map(r=> ({ x: new Date(r.timestamp).getTime(), y: Number(r.volume || 0) }));
        setHistorical({ labels, prices, volumes, ohlc, candles, volumesXY });
        // update display price and percent based on history (last vs previous)
        if(prices.length){
          const last = prices[prices.length - 1];
          const prev = prices.length > 1 ? prices[prices.length - 2] : (data?.close ?? last);
          setDisplayPrice(last);
          try{
            const pct = prev ? (last - prev) / prev * 100 : null;
            setDisplayChangePct(pct);
          }catch(e){ setDisplayChangePct(null); }
        }
      }catch(e){
        setHistorical(null);
      }finally{ setLoadingHistory(false); }
    })();
  }, [symbol, selectedRange]);

  useEffect(()=>{
    // compute badge position for latest price when chart and historical data are ready
    function updateBadge(){
      try{
        const chart = chartRef.current?.chart || chartRef.current; // react-chartjs-2 may expose .chart
        if(!chart || !historical || !historical.prices?.length || !panelRef.current) return;
        const latest = historical.prices[historical.prices.length - 1];
        const yScale = chart.scales?.y || chart.scales?.y0 || null;
        const xScale = chart.scales?.x || chart.scales?.x0 || null;
        if(!yScale || !chart.canvas) return;
        const pixel = yScale.getPixelForValue(latest);
        const canvasRect = chart.canvas.getBoundingClientRect();
        const panelRect = panelRef.current.getBoundingClientRect();
        const top = pixel + (canvasRect.top - panelRect.top);
        setBadgeTop(top);
        // compute connector from chart point to badge
        if(xScale){
          try{
            const px = xScale.getPixelForValue(historical.prices.length - 1);
            const pointX = px + (canvasRect.left - panelRect.left);
            // badge position
            let badgeLeft = null;
            if(badgeRef.current){
              const bRect = badgeRef.current.getBoundingClientRect();
              badgeLeft = bRect.left - panelRect.left + 0; // left coordinate
            } else {
              // estimate badge left from panel width and right offset 10
              const bw = 80;
              badgeLeft = panelRect.width - 10 - bw;
            }
            const left = Math.min(pointX, badgeLeft);
            const width = Math.max(2, Math.abs(badgeLeft - pointX));
            setConnector({ left, width, top });
          }catch(e){ setConnector(null); }
        }
      }catch(err){
        // ignore
      }
    }

    updateBadge();
    window.addEventListener('resize', updateBadge);
    const id = setInterval(updateBadge, 800);
    return ()=>{ window.removeEventListener('resize', updateBadge); clearInterval(id); };
  }, [historical]);

  const headerChangePct = useMemo(() => {
    // Use real-time change if available, otherwise calculate from real-time price vs previous close
    if (realTimeData?.change_percent !== undefined) {
      return realTimeData.change_percent;
    }
    if (realTimeData?.price && realTimeData?.close) {
      return ((realTimeData.price - realTimeData.close) / realTimeData.close) * 100;
    }
    // Fallback to displayChangePct or data change_percent
    return displayChangePct ?? data?.change_percent ?? null;
  }, [realTimeData, displayChangePct, data?.change_percent]);
  const changeColor = headerChangePct === null ? 'var(--text-secondary)' : (headerChangePct >= 0 ? '#10b981' : '#ef4444');

  return (
    <div className="stock-detail-page" style={{padding:'1rem', maxWidth:1200, margin:'0 auto'}}>
      {/* Toasts container */}
      <div style={{position:'fixed', right:16, top:16, zIndex:9999, display:'flex', flexDirection:'column', gap:8}}>
        {toasts.map(t => (
          <div key={t.id} style={{padding:10, borderRadius:8, background: t.kind === 'error' ? 'rgba(239,68,68,0.95)' : (t.kind === 'success' ? 'rgba(16,185,129,0.95)' : 'rgba(55,65,81,0.95)'), color:'#04201a', minWidth:180}}>{t.message}</div>
        ))}
      </div>
      <button
        aria-label={t && typeof t === 'function' ? (t('close') || 'Close') : 'Close'}
        title={t && typeof t === 'function' ? (t('close') || 'Close') : 'Close'}
        onClick={() => navigate('/')}
        style={{position:'fixed', top:12, right:12, zIndex:9999, background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', padding:'8px 10px', borderRadius:8, cursor:'pointer'}}
      >
        ✕
      </button>
      <div className="stock-detail-header">
        <div className="left">
          <div style={{fontSize:12, color:'var(--text-secondary)'}}>{(data?.exchange || '') + (data?.exchange ? ' · ' : '') + (data?.currency || '')}</div>
          <div className="symbol">{symbol}</div>
          <div className="company-name">{data?.name || ''}</div>
        </div>
        <div className="stock-header-right">
            {/* <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <button onClick={()=>{
                try{
                  const s = JSON.parse(window.localStorage.getItem('market360.follows')||'[]');
                  const next = Array.isArray(s)?s.slice():[];
                  const idx = next.indexOf(symbol);
                  if(idx === -1) next.push(symbol); else next.splice(idx,1);
                  window.localStorage.setItem('market360.follows', JSON.stringify(next));
                  setFollowed(idx === -1);
                }catch(e){ setFollowed(!followed); }
              }} className="action-btn" style={{background: followed ? '#0b1220' : 'transparent', border: followed ? '1px solid rgba(255,255,255,0.06)' : '1px solid var(--border-color)', padding:'6px 10px', borderRadius:10, color: followed ? '#9feadf' : 'var(--text-primary)'}}>
              {followed ? `✓ ${t('following')}` : `☆ ${t('follow')}`}
            </button>
            <button onClick={handleAnalyze} className="action-btn" style={{background:'linear-gradient(90deg,#10b981,#14b8a6)', border:'none', padding:'8px 12px', borderRadius:10, color:'#04201a', fontWeight:700}}>{t('analyze.button')}</button>
            <select value={chartType} onChange={e=>setChartType(e.target.value)} style={{background:'var(--bg-glass)', border:'1px solid var(--border-color)', padding:'6px 8px', borderRadius:8, color:'var(--text-primary)'}}>
              <option value="line">Line</option>              
              <option value="bar">Bar</option>
              <option value="candle">Candle</option>
            </select>
          </div> */}

          {/* <div className="price-row" style={{marginTop:12}}>
            <div className="price-large">{loadingDetails ? t('loading') : (displayPrice ? fmtCurrency(displayPrice, currency) : (realTimeData?.price ? fmtCurrency(realTimeData.price, currency) : (data?.price ? fmtCurrency(data.price, currency) : '—')))}</div>
            <div className="pct" style={{color: changeColor, marginLeft:8}}>{headerChangePct !== null ? (headerChangePct >= 0 ? '+' : '') + Number(headerChangePct).toFixed(2) + '%' : '—'}</div>
          </div> */}

          {/* removed duplicate small detail block — moved Last update into price card */}
        </div>
      </div>

      <div className="stock-detail-grid">
        <aside className="stock-detail-side">
          <div className="price-card" style={{background:'var(--bg-glass)', padding:12, borderRadius:12, border:'1px solid var(--border-color)'}}>
            <div style={{fontSize:20, fontWeight:800}}>{loadingDetails ? t('loading') : fmtCurrency(realTimeData?.price || data?.price, currency)}</div>
            <div style={{marginTop:6, color: changeColor, fontWeight:700}}>{headerChangePct !== null ? (headerChangePct >= 0 ? '+' : '') + Number(headerChangePct).toFixed(2) + '%' : '—'}</div>
            <div style={{marginTop:10, fontSize:13, color:'var(--text-secondary)'}}>
              <div>Open: {realTimeData?.open ? fmtCurrency(realTimeData.open, currency) : (data?.open ? fmtCurrency(data.open, currency) : '—')}</div>
              <div>Prev Close: {realTimeData?.close ? fmtCurrency(realTimeData.close, currency) : (data?.close ? fmtCurrency(data.close, currency) : '—')}</div>
              <div>Bid/Ask: {realTimeData?.bid ? fmtCurrency(realTimeData.bid, currency) : (data?.bid ? fmtCurrency(data.bid, currency) : '—')} / {realTimeData?.ask ? fmtCurrency(realTimeData.ask, currency) : (data?.ask ? fmtCurrency(data.ask, currency) : '—')}</div>
            </div>
            {lastUpdateTime && <div style={{marginTop:10, fontSize:12, color:'var(--text-secondary)'}}>Last update: {lastUpdateTime.toLocaleString()}</div>}
          </div>

          <div style={{marginTop:12}}>
            <div className="stats-grid" style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8}}>
              <div className="stat" style={{background:'var(--bg-tertiary)', padding:10, borderRadius:8}}>52W H<div style={{fontWeight:700}}> {data?.high52 ? fmtCurrency(data.high52) : '—'}</div></div>
              <div className="stat" style={{background:'var(--bg-tertiary)', padding:10, borderRadius:8}}>52W L<div style={{fontWeight:700}}> {data?.low52 ? fmtCurrency(data.low52) : '—'}</div></div>
              <div className="stat" style={{background:'var(--bg-tertiary)', padding:10, borderRadius:8}}>Volume<div style={{fontWeight:700}}> {data?.volume ? fmtShortNumber(data.volume) : '—'}</div></div>
              <div className="stat" style={{background:'var(--bg-tertiary)', padding:10, borderRadius:8}}>Market Cap<div style={{fontWeight:700}}> {data?.marketCap ? fmtShortNumber(data.marketCap) : '—'}</div></div>
            </div>
            {/* Suggestion panel (from persisted runner) */}
            <div style={{marginTop:12}}>
              <div style={{background:'var(--bg-tertiary)', padding:10, borderRadius:8, border:'1px solid var(--border-color)'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div style={{fontWeight:700}}>Suggestion</div>
                  {loadingSuggestion ? <div style={{fontSize:12, color:'var(--text-secondary)'}}>Loading…</div> : null}
                </div>
                <div style={{marginTop:8}}>
                  {suggestion ? (
                    <div style={{fontSize:13, lineHeight:1.4}}>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          {suggestion.regime ? (
                            <span style={{padding:'6px 10px', borderRadius:8, fontWeight:800, background: suggestion.regime === 'bull' ? '#d1fae5' : suggestion.regime === 'bear' ? '#fee2e2' : '#f0f9ff', color: suggestion.regime === 'bull' ? '#065f46' : suggestion.regime === 'bear' ? '#991b1b' : '#0369a1'}}>{String(suggestion.regime).toUpperCase()}</span>
                          ) : (
                            <span style={{color:'var(--text-secondary)'}}>No regime</span>
                          )}
                          <div style={{color:'var(--text-secondary)', fontSize:12}}>Updated: {suggestion.created_at ? new Date(suggestion.created_at).toLocaleString() : ''}</div>
                        </div>
                        <div style={{fontSize:12, color:'var(--text-secondary)'}}>Source: automated runner</div>
                      </div>

                      {/* Signals */}
                      <div style={{marginTop:10}}>
                        <div style={{fontWeight:700, marginBottom:6}}>Signals</div>
                        {Array.isArray(suggestion.signals) && suggestion.signals.length ? (
                          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                            {suggestion.signals.map((s, i) => (
                              <div key={i} style={{background:'rgba(255,255,255,0.03)', padding:'6px 8px', borderRadius:8, fontSize:12}}>
                                {STRATEGY_META[s]?.title || s}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{color:'var(--text-secondary)'}}>No signals detected</div>
                        )}
                      </div>

                      {/* News: show only title and summary; title opens original article */}
                      {/* normalizeNewsItem helper defined at module scope */}
                      <div style={{marginTop:12}}>
                        <div style={{fontWeight:700, marginBottom:6}}>News</div>
                        {Array.isArray(suggestion.news) && suggestion.news.length ? (
                          suggestion.news.slice(0,3).map((n, idx) => {
                            const { title, summary, url } = normalizeNewsItem(n);
                            return (
                              <div key={idx} style={{marginBottom:8, background:'rgba(255,255,255,0.02)', padding:8, borderRadius:6}}>
                                {url ? (
                                  <a href={url} target="_blank" rel="noreferrer" style={{fontSize:13, fontWeight:700, color:'#60a5fa', textDecoration:'none'}}>{title}</a>
                                ) : (
                                  <div style={{fontSize:13, fontWeight:700}}>{title}</div>
                                )}
                                {summary ? <div style={{marginTop:8, fontSize:13, color:'var(--text-primary)'}}>{summary}</div> : null}
                              </div>
                            );
                          })
                        ) : (
                          <div style={{color:'var(--text-secondary)'}}>No recent news.</div>
                        )}
                      </div>

                      {/* LLM Review */}
                      <div style={{marginTop:12}}>
                        <div style={{fontWeight:700, marginBottom:6}}>AI Suggestion:</div>
                        {suggestion.llm_review ? (
                          (() => {
                            const r = typeof suggestion.llm_review === 'string' ? (() => { try { return JSON.parse(suggestion.llm_review); } catch { return { summary: suggestion.llm_review }; } })() : suggestion.llm_review;
                            return (
                              <div style={{background:'rgba(255,255,255,0.02)', padding:10, borderRadius:8}}>
                                <div><strong>Decision:</strong> {String(r.decision || r.summary || 'proceed_with_caution').toUpperCase()}</div>
                                {r.confidence !== undefined ? <div><strong>Confidence:</strong> {(Number(r.confidence) * 100).toFixed(0)}%</div> : null}
                                {Array.isArray(r.risk_factors) && r.risk_factors.length ? <div style={{marginTop:6}}><strong>Risk factors:</strong> {r.risk_factors.join(', ')}</div> : null}
                                {Array.isArray(r.notes) && r.notes.length ? (
                                  <div style={{marginTop:6}}>
                                    <strong>Notes:</strong>
                                    <ul style={{marginTop:6, paddingLeft:18}}>
                                      {r.notes.map((n,i)=> <li key={i} style={{fontSize:13}}>{n}</li>)}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()
                        ) : (
                          <div style={{color:'var(--text-secondary)'}}>No LLM review available.</div>
                        )}
                      </div>

                      {/* raw JSON display removed per request */}
                    </div>
                  ) : (
                    <div style={{color:'var(--text-secondary)'}}>No suggestion available.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </aside>
        
        <section className="stock-detail-main">
          <div style={{marginBottom:12}}>
            <button onClick={() => setShowDebug(d => !d)} style={{padding:'6px 10px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)', cursor:'pointer'}}>
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </button>
            {showDebug && (
              <div style={{marginTop:8, background:'var(--bg-tertiary)', padding:10, borderRadius:8, border:'1px solid var(--border-color)', maxHeight:300, overflow:'auto', whiteSpace:'pre-wrap'}}>
                <div style={{fontWeight:700, marginBottom:6}}>Debug: predictions</div>
                <pre style={{fontSize:12, color:'var(--text-secondary)'}}>{JSON.stringify(predictions || {}, null, 2)}</pre>
                <div style={{fontWeight:700, marginTop:8, marginBottom:6}}>Debug: strategiesData</div>
                <pre style={{fontSize:12, color:'var(--text-secondary)'}}>{JSON.stringify(strategiesData || {}, null, 2)}</pre>
              </div>
            )}
          </div>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
            <div style={{display:'flex', gap:8}}>
              {['1D','1W','1M','3M','6M','YTD','1Y','2Y','5Y','All'].map(p=> (
                <button key={p} onClick={()=>setSelectedRange(p)} className={`time-tab ${selectedRange===p ? 'active' : ''}`}>{p}</button>
              ))}
            </div>
            {/* overlays moved to below the chart (near x-axis) */}
          </div>

          <div className="chart-panel" style={{background:'var(--bg-glass)', padding:12, borderRadius:12, border:'1px solid var(--border-color)'}}>
            {loadingHistory ? (
              <div style={{padding:20, textAlign:'center'}}>{t('loading')}</div>
            ) : historical && historical.prices && historical.prices.length ? (
              (() => {
                // Use time-based datasets so price and volume share the same x-axis
                const priceXY = historical.candles ? historical.candles.map(c=>({ x: c.x, y: c.c })) : historical.labels.map((lab, i)=>({ x: lab, y: historical.prices[i] }));
                const volXY = historical.volumesXY && historical.volumesXY.length ? historical.volumesXY : (historical.volumes && historical.labels ? historical.labels.map((lab,i)=>({ x: lab, y: historical.volumes[i] })) : []);

                const volColors = (historical.candles || historical.ohlc) ? (historical.candles || historical.ohlc).map((c,i)=> {
                  const up = (c.c ?? c.close) >= (c.o ?? c.open);
                  return up ? 'rgba(6,95,70,0.32)' : 'rgba(220,38,38,0.32)';
                }) : (historical.volumes || []).map(()=> 'rgba(100,116,139,0.24)');

                // Price dataset
                const priceDataset = (()=>{
                  const base = {
                    label: `${symbol} Price`,
                    data: priceXY,
                    parsing: false,
                    borderColor: '#14b8a6',
                    tension: 0.3,
                    backgroundColor: 'rgba(20,184,166,0.08)',
                    yAxisID: 'price'
                  };
                  if(chartType === 'mountain') return { ...base, fill: true, pointRadius: 0, borderWidth: 2 };
                  if(chartType === 'bar') return { ...base, type: 'bar', backgroundColor: 'rgba(20,184,166,0.16)', borderColor: 'rgba(20,184,166,0.9)', barPercentage: 0.8 };
                  // default line
                  return { ...base, fill: false, pointRadius: 0, borderWidth: 2 };
                })();

                // Candle dataset if requested
                const candleDataset = chartType === 'candle' ? {
                  label: `${symbol} Candles`,
                  data: historical.candles,
                  parsing: false,
                  type: 'candlestick',
                  color: { up: 'rgba(6,95,70,0.95)', down: 'rgba(220,38,38,0.95)' },
                  borderColor: 'rgba(2,6,23,0.9)',
                  borderWidth: 1,
                  yAxisID: 'price'
                } : null;

                const volumeDataset = {
                  label: 'Volume',
                  data: volXY,
                  parsing: false,
                  type: 'bar',
                  yAxisID: 'volume',
                  backgroundColor: volColors,
                  barThickness: 'flex',
                  maxBarThickness: 18
                };

                // compute overlays (MA, RSI) from historical closes
                const closes = historical.candles && historical.candles.length ? (historical.candles.map(c=> Number(c.c))) : (historical.prices || []).map(p=> Number(p));
                const xs = historical.candles && historical.candles.length ? historical.candles.map(c=> c.x) : (historical.labels || []).map((l,i)=> l);

                function computeSMA(values, len){
                  const out = [];
                  for(let i=0;i<values.length;i++){
                    if(i < len - 1){ out.push(null); continue; }
                    let sum = 0;
                    for(let j=0;j<len;j++) sum += values[i-j] || 0;
                    out.push(sum / len);
                  }
                  return out;
                }

                function computeRSI(values, period=14){
                  const out = [];
                  for(let i=0;i<values.length;i++){
                    if(i < period){ out.push(null); continue; }
                    let gains = 0, losses = 0;
                    for(let j=i-period+1;j<=i;j++){
                      const diff = values[j] - values[j-1];
                      if(isNaN(diff)) continue;
                      if(diff > 0) gains += diff; else losses += Math.abs(diff);
                    }
                    const avgGain = gains / period;
                    const avgLoss = losses / period;
                    const rs = avgLoss === 0 ? (avgGain === 0 ? 0 : 9999) : (avgGain / avgLoss);
                    const rsi = avgLoss === 0 && avgGain === 0 ? 50 : (100 - (100 / (1 + rs)));
                    out.push(rsi);
                  }
                  return out;
                }

                function computeStdDev(values, len){
                  const out = [];
                  for(let i=0;i<values.length;i++){
                    if(i < len - 1){ out.push(null); continue; }
                    let sum = 0; let mean = 0;
                    for(let j=0;j<len;j++) sum += values[i-j] || 0;
                    mean = sum / len;
                    let ssd = 0;
                    for(let j=0;j<len;j++){ const v = values[i-j] || 0; ssd += Math.pow(v - mean, 2); }
                    out.push(Math.sqrt(ssd / len));
                  }
                  return out;
                }

                function computeATR(highs, lows, closes, period=14){
                  const trs = [];
                  for(let i=0;i<highs.length;i++){
                    const high = highs[i] || 0;
                    const low = lows[i] || 0;
                    const prevClose = i>0 ? (closes[i-1]||0) : (closes[i]||0);
                    const t1 = high - low;
                    const t2 = Math.abs(high - prevClose);
                    const t3 = Math.abs(low - prevClose);
                    trs.push(Math.max(t1, t2, t3));
                  }
                  // simple SMA of TR as ATR
                  const atr = [];
                  for(let i=0;i<trs.length;i++){
                    if(i < period - 1){ atr.push(null); continue; }
                    let sum = 0; for(let j=0;j<period;j++) sum += trs[i-j] || 0;
                    atr.push(sum / period);
                  }
                  return atr;
                }

                const ma5 = computeSMA(closes, 5).map((v,i)=> ({ x: xs[i], y: v }));
                const ma20 = computeSMA(closes, 20).map((v,i)=> ({ x: xs[i], y: v }));
                const ma50 = computeSMA(closes, 50).map((v,i)=> ({ x: xs[i], y: v }));
                const ma200 = computeSMA(closes, 200).map((v,i)=> ({ x: xs[i], y: v }));
                const rsiArr = computeRSI(closes, 14).map((v,i)=> ({ x: xs[i], y: v }));

                // Bollinger Bands (20, 2) — use precomputed ma20
                const bbStd = computeStdDev(closes, 20);
                const ma20vals = computeSMA(closes,20);
                const bbUpper = bbStd.map((sd,i)=> {
                  const mid = ma20vals[i];
                  return mid == null || sd == null ? null : (mid + 2 * sd);
                }).map((v,i)=> ({ x: xs[i], y: v }));
                const bbLower = bbStd.map((sd,i)=> {
                  const mid = ma20vals[i];
                  return mid == null || sd == null ? null : (mid - 2 * sd);
                }).map((v,i)=> ({ x: xs[i], y: v }));

                // ATR (14)
                const highs = historical.candles && historical.candles.length ? historical.candles.map(c=> Number(c.h)) : (historical.ohlc || []).map(o=> Number(o.high));
                const lows = historical.candles && historical.candles.length ? historical.candles.map(c=> Number(c.l)) : (historical.ohlc || []).map(o=> Number(o.low));
                const atrArrVals = computeATR(highs, lows, closes, 14);
                const atrArr = atrArrVals.map((v,i)=> ({ x: xs[i], y: v }));

                const datasets = [];
                if(candleDataset) datasets.push(candleDataset); else datasets.push(priceDataset);

                // push moving averages if enabled
                if(overlays.ma5) datasets.push({ label: 'MA5', data: ma5, parsing: false, borderColor: '#f97316', borderWidth: 1.5, pointRadius: 0, yAxisID: 'price', spanGaps: true });
                if(overlays.ma20) datasets.push({ label: 'MA20', data: ma20, parsing: false, borderColor: '#60a5fa', borderWidth: 1.5, pointRadius: 0, yAxisID: 'price', spanGaps: true });
                if(overlays.ma50) datasets.push({ label: 'MA50', data: ma50, parsing: false, borderColor: '#a78bfa', borderWidth: 1.5, pointRadius: 0, yAxisID: 'price', spanGaps: true });
                if(overlays.ma200) datasets.push({ label: 'MA200', data: ma200, parsing: false, borderColor: '#94a3b8', borderWidth: 1.5, pointRadius: 0, yAxisID: 'price', spanGaps: true });

                // Bollinger Bands overlay
                if(overlays.bb){
                  // BB Upper (fill down to BB Lower) - use '+1' to fill to next dataset (BB Lower)
                  datasets.push({ label: 'BB Upper', data: bbUpper, parsing: false, borderColor: 'rgba(99,102,241,0.9)', borderWidth: 1, pointRadius: 0, yAxisID: 'price', spanGaps: true, fill: '+1', backgroundColor: 'rgba(99,102,241,0.08)' });
                  // BB Lower (the target for fill). Keep visible border but no fill.
                  datasets.push({ label: 'BB Lower', data: bbLower, parsing: false, borderColor: 'rgba(99,102,241,0.5)', borderWidth: 1, pointRadius: 0, yAxisID: 'price', spanGaps: true, fill: false, backgroundColor: 'rgba(99,102,241,0.04)' });
                }

                // ATR overlay
                if(overlays.atr){
                  datasets.push({ label: 'ATR', data: atrArr, parsing: false, borderColor: '#f59e0b', borderWidth: 1, pointRadius: 0, yAxisID: 'atr', spanGaps: true });
                }

                // push RSI as separate axis if enabled
                if(overlays.rsi) datasets.push({ label: 'RSI', data: rsiArr, parsing: false, borderColor: '#fb7185', borderWidth: 1, pointRadius: 0, yAxisID: 'rsi', spanGaps: true });

                datasets.push(volumeDataset);

                const chartData = { datasets };

                // resolve CSS variables for chart text colors so canvas gets real color values
                const _css = typeof window !== 'undefined' ? getComputedStyle(document.body) : null;
                const tickColor = (_css && _css.getPropertyValue('--text-secondary')) ? _css.getPropertyValue('--text-secondary').trim() : '#94a3b8';
                const titleColor = (_css && _css.getPropertyValue('--text-secondary')) ? _css.getPropertyValue('--text-secondary').trim() : '#94a3b8';

                const chartOptions = {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      callbacks: {
                        title: items => items && items.length ? (items[0].parsed && items[0].parsed.x ? new Date(items[0].parsed.x).toLocaleString() : items[0].label) : '',
                        label: ctx => {
                          try{
                            const parsed = ctx.parsed;
                            if(ctx.dataset && ctx.dataset.type === 'candlestick'){
                              // Chartjs financial provides o/h/l/c in parsed
                              return ['O: ' + fmtCurrency(parsed.o, currency), 'H: ' + fmtCurrency(parsed.h, currency), 'L: ' + fmtCurrency(parsed.l, currency), 'C: ' + fmtCurrency(parsed.c, currency)];
                            }
                            if(ctx.dataset && ctx.dataset.label === 'Volume'){
                              return 'Vol: ' + fmtShortNumber(parsed.y);
                            }
                            // price point
                            return fmtCurrency(parsed.y, currency);
                          }catch(e){ return '' }
                        }
                      }
                    },
                    // zoom: {
                    //   pan: { enabled: true, mode: 'x' },
                    //   zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' }
                    // }
                  },
                  scales: {
                    x: { type: 'time', grid: { color: 'rgba(200,200,200,0.06)' }, ticks: { color: tickColor } },
                    price: { position: 'right', grid: { drawBorder: false, color: 'rgba(200,200,200,0.06)' }, ticks: { color: tickColor, callback: v => 'USD' + Number(v).toFixed(0) }, beginAtZero: false, title: { display: true, text: symbol + ' Price', color: titleColor } },
                    rsi: { display: overlays.rsi, position: 'left', min: 0, max: 100, grid: { drawOnChartArea: false }, ticks: { color: tickColor } },
                    atr: { display: overlays.atr, position: 'left', grid: { drawOnChartArea: false }, ticks: { color: tickColor } },
                    volume: { position: 'left', grid: { display: false }, ticks: { display: false }, beginAtZero: true }
                  },
                  elements: { point: { radius: 0, hoverRadius: 4 } },
                  layout: { padding: { right: 32, bottom: 6 } }
                };

                return <ReactChart key={`${symbol}-${chartType}`} ref={chartRef} type={chartType === 'candle' ? 'candlestick' : 'line'} data={chartData} options={chartOptions} />;
              })()
            ) : (
              <div style={{padding:20, textAlign:'center'}}>{error ? 'Unable to load data' : 'No historical data'}</div>
            )}
          </div>
          {badgeTop !== null && historical && historical.prices && historical.prices.length ? (
            <>
              <div ref={badgeRef} className={'chart-badge ' + (headerChangePct !== null && headerChangePct >= 0 ? 'positive' : 'negative')} style={{position:'absolute', right:10, top: badgeTop, transform:'translateY(-50%)'}}>
                <div style={{fontSize:12}}>{fmtCurrency(historical.prices[historical.prices.length-1])}</div>
                <div style={{fontSize:11, opacity:0.95}}>{headerChangePct !== null ? (headerChangePct >= 0 ? '+' : '') + Number(headerChangePct).toFixed(2) + '%' : ''}</div>
              </div>
              {connector ? (
                <div className="chart-connector" style={{position:'absolute', left: connector.left, top: connector.top - 1, width: connector.width, height:2, background:'rgba(20,184,166,0.6)', zIndex:25}} />
              ) : null}
            </>
          ) : null}

          {/* Overlays controls moved below the chart, near the x-axis */}
          <div style={{marginTop:10, display:'flex', justifyContent:'center', alignItems:'center', gap:8}}>
            <div style={{display:'flex', gap:8, alignItems:'center'}}>     
              {/* <div style={{fontSize:12, color:'var(--text-secondary)', marginRight:6}}>Chart type</div>        */}
              <select value={chartType} onChange={e=>setChartType(e.target.value)} style={{background:'var(--bg-glass)', border:'1px solid var(--border-color)', padding:'6px 8px', borderRadius:8, color:'var(--text-primary)'}}>
                <option value="line">Line</option>                            
                <option value="candle">Candle</option>
                <option value="bar">Bar</option>
              </select>
            </div>
            {/* <div style={{fontSize:12, color:'var(--text-secondary)', marginRight:6}}>Overlays</div> */}
              {(() => {
                const overlayColors = { ma5:'#f97316', ma20:'#60a5fa', ma50:'#a78bfa', ma200:'#94a3b8', rsi:'#fb7185', bb:'#34d399', atr:'#f472b6' };
                return [
                  {k:'ma5', label:'MA5'}, {k:'ma20', label:'MA20'}, {k:'ma50', label:'MA50'}, {k:'ma200', label:'MA200'}, {k:'rsi', label:'RSI'}, {k:'bb', label:'BB'}, {k:'atr', label:'ATR'}
                ].map(o => {
                  const isActive = Boolean(overlays[o.k]);
                  return (
                    <button
                      key={o.k}
                      onClick={()=>setOverlays(s => ({...s, [o.k]: !s[o.k]}))}
                      className={`overlay-btn ${isActive ? 'active' : ''}`}
                      style={{
                        padding:'6px 8px',
                        borderRadius:6,
                        fontSize:12,
                        background: isActive ? overlayColors[o.k] : 'var(--bg-secondary)',
                        color: isActive ? '#ffffff' : 'var(--text-primary)',
                        border: isActive ? 'none' : '1px solid var(--border-color)'
                      }}
                    >{o.label}</button>
                  );
                });
              })()}
          </div>

          {/* external volume chart removed — volume is now inside the main chart */}

          <div className="historical-block" style={{marginTop:12}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div style={{fontSize:14, fontWeight:800, color:'var(--text-secondary)'}}>Historical Prices</div>
              <div>
                <button onClick={() => setShowHistoricalBlock(!showHistoricalBlock)} className="action-btn" style={{padding:'6px 10px', borderRadius:8, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}}>{showHistoricalBlock ? 'Hide' : 'Show'}</button>
              </div>
            </div>

            {showHistoricalBlock && (
              <div key={`hist-${selectedRange}`} style={{marginTop:8, maxHeight:220, overflowY:'auto', borderRadius:8, border:'1px solid var(--border-color)', background:'var(--bg-tertiary)'}}>
                {historical && ( (historical.candles && historical.candles.length) || (historical.prices && historical.prices.length) ) ? (
                  (() => {
                    // Build rows preferring candles (which include open/high/low/close/volume)
                    const rows = historical.candles && historical.candles.length ? historical.candles : (historical.prices || []).map((p,i)=>({ x: historical.labels && historical.labels[i] ? historical.labels[i] : i, open: null, high: null, low: null, close: p, volume: null }));
                    // render as table with columns: Date Time, Open, Close, High, Low, Vol
                    return (
                      <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{borderBottom:'1px solid var(--border-color)'}}>
                            <th style={{textAlign:'left', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Date Time</th>
                            <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Open</th>
                            <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Close</th>
                            <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>High</th>
                            <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Low</th>
                            <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Vol</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.slice().reverse().map((r, idx) => {
                            // r may be candle object or simple mapping
                            const dateLabel = (() => { try{ const t = typeof r.x === 'number' ? new Date(r.x) : new Date(r.x); return isNaN(t.getTime()) ? String(r.x) : t.toLocaleString(); }catch(e){ return String(r.x) }})();
                            const openVal = (r.o ?? r.open ?? null);
                            const highVal = (r.h ?? r.high ?? null);
                            const lowVal = (r.l ?? r.low ?? null);
                            const closeVal = (r.c ?? r.close ?? r.price ?? null);
                            const volVal = (r.v ?? r.volume ?? r.vol ?? r.Volume ?? null);
                            return (
                              <tr key={idx} style={{borderBottom:'1px solid var(--border-color)'}}>
                                <td style={{padding:8, fontSize:12, color:'var(--text-secondary)'}}>{dateLabel}</td>
                                    <td style={{padding:8, textAlign:'right'}}>
                                      {openVal !== null ? (
                                        <span style={{color: (closeVal !== null && closeVal > openVal ? '#10b981' : '#ef4444') }}>{fmtCurrency(openVal, currency)}</span>
                                      ) : '—'}
                                    </td>
                                    <td style={{padding:8, textAlign:'right', fontWeight:700}}>
                                      {closeVal !== null ? (
                                        <span style={{color: (openVal !== null && closeVal > openVal ? '#10b981' : '#ef4444') }}>{fmtCurrency(closeVal, currency)}</span>
                                      ) : '—'}
                                    </td>
                                    <td style={{padding:8, textAlign:'right'}}>{highVal !== null ? <span style={{color:'#60a5fa'}}>{fmtCurrency(highVal, currency)}</span> : '—'}</td>
                                    <td style={{padding:8, textAlign:'right'}}>{lowVal !== null ? <span style={{color:'#60a5fa'}}>{fmtCurrency(lowVal, currency)}</span> : '—'}</td>
                                    <td style={{padding:8, textAlign:'right'}}>{volVal !== null ? fmtShortNumber(volVal) : '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    );
                  })()
                ) : (
                  <div style={{padding:12, color:'var(--text-secondary)'}}>No historical prices</div>
                )}
              </div>
            )}
          </div>

          <div style={{marginTop:12, display:'flex', gap:12, justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap'}}>
            <KeyMetrics data={data} indicators={indicators} loading={loadingDetails || loadingIndicators} t={t} currency={currency} />
          </div>

          <div ref={aiRef} className="ai-panel" style={{marginTop:16, background:'var(--bg-tertiary)', padding:12, borderRadius:8, border:'1px solid var(--border-color)'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
              <div style={{fontSize:18, fontWeight:900, color:'#10b981'}}>Phân tích AI</div>
              <div>
                <button onClick={handleAnalyze} className="action-btn" style={{padding:'6px 10px', borderRadius:8, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}}>{aiLoading ? t('loading') : t('analyze.rerun')}</button>
              </div>
            </div>

            {aiLoading ? (
              <div style={{padding:12}}>{t('loading')}</div>
            ) : aiError ? (
              <div style={{padding:12, color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <div style={{flex:1}}>Error: {aiError}</div>
                <div style={{marginLeft:12}}>
                  <button onClick={handleAnalyze} className="action-btn" style={{padding:'6px 10px', borderRadius:8, border:'1px solid var(--border-color)', background:'#111827', color:'#10b981'}}>Retry</button>
                </div>
              </div>
            ) : (
              <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:12}}>
                <div>
                  {/* Moved: Strategy Explanations & Trade Formulas (hidden by default) */}
                  {/* Strategy explanations and trade-formula listings removed — shown per-strategy in results area */}
                  <div style={{marginBottom:8}}>
                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
                      <div style={{fontSize:14, fontWeight:700}}>{t('ai.insights')}</div>
                      {aiTranslated ? <div style={{fontSize:12, color:'var(--text-secondary)', background:'rgba(255,255,255,0.03)', padding:'2px 6px', borderRadius:6}}>{t('ai.translated_badge')}</div> : null}
                    </div>
                    {aiInsights ? (
                      Array.isArray(aiInsights) ? (
                        <ul style={{margin:0, paddingLeft:18}}>
                          {aiInsights.map((it,i)=> <li key={i} style={{marginBottom:6}}>{it}</li>)}
                        </ul>
                      ) : (
                        <div style={{margin:0, paddingLeft:0}}>{String(aiInsights)}</div>
                      )
                    ) : (
                      <div style={{color:'var(--text-secondary)'}}>No insights available</div>
                    )}
                  </div>

                </div>

              </div>
            )}

            {/* Backtest Runner & Results */}
            <div className="backtest-panel" style={{marginTop:16, background:'var(--bg-tertiary)', padding:12, borderRadius:8, border:'1px solid var(--border-color)'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                <div style={{fontSize:18, fontWeight:900, color:'#3b82f6'}}>Backtest</div>
                <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
                  <div style={{display:'flex', gap:8, alignItems:'center', minWidth:0}}>
                    <label style={{fontSize:12, color:'var(--text-secondary)'}}>Initial equity</label>
                    <input type="number" value={backtestInitialEquity} onChange={e=>setBacktestInitialEquity(Number(e.target.value) || 0)} style={{minWidth:0, maxWidth:120, width:'auto', padding:'6px 8px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}} />
                  </div>
                  <div style={{display:'flex', gap:8, alignItems:'center', minWidth:0}}>
                    <label style={{fontSize:12, color:'var(--text-secondary)'}}>Risk %</label>
                    <input type="number" step="0.01" value={(backtestRiskPct*100).toString()} onChange={e=>setBacktestRiskPct((Number(e.target.value) || 0)/100)} style={{minWidth:0, maxWidth:80, width:'auto', padding:'6px 8px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}} />
                  </div>
                  <div style={{display:'flex', gap:8, alignItems:'center', minWidth:0}}>
                    <label style={{fontSize:12, color:'var(--text-secondary)'}}>Benchmark</label>
                    <select value={marketBenchmark} onChange={e=>setMarketBenchmark(e.target.value)} style={{minWidth:0, maxWidth:120, padding:'6px 8px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}}>
                      <option value="SPY">SPY</option>
                      <option value="QQQ">QQQ</option>
                      <option value="DIA">DIA</option>
                    </select>
                  </div>
                  <button
                    onClick={()=>runBacktest({ initial_equity: backtestInitialEquity, risk_pct: backtestRiskPct })}
                    disabled={loadingBacktest || !historical}
                    style={{padding:'8px 12px', borderRadius:6, border:'1px solid var(--border-color)', background: loadingBacktest ? 'var(--bg-tertiary)' : '#3b82f6', color: loadingBacktest ? 'var(--text-secondary)' : 'white', cursor: loadingBacktest ? 'not-allowed' : 'pointer', flexShrink:0}}
                  >{loadingBacktest ? 'Running...' : 'Run Backtest'}</button>
                </div>
              </div>

              {loadingBacktest ? (
                <div style={{padding:12}}>{t('loading')}</div>
              ) : backtestData ? (
                (() => {
                  const inR = backtestData.in_sample || null;
                  const outR = backtestData.out_of_sample || null;
                  const renderMetrics = (m) => (
                    <table style={{fontSize:13, borderCollapse:'collapse'}}>
                      <tbody>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Total trades</td><td style={{padding:6}}>{m.total_trades}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Win rate</td><td style={{padding:6}}>{(m.win_rate || 0).toFixed(2)}%</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Avg win</td><td style={{padding:6}}>{(m.avg_win || 0).toFixed(2)}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Avg loss</td><td style={{padding:6}}>{(m.avg_loss || 0).toFixed(2)}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Risk-reward</td><td style={{padding:6}}>{(m.risk_reward || 0).toFixed(2)}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Expectancy</td><td style={{padding:6}}>{(m.expectancy || 0).toFixed(4)}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Profit factor</td><td style={{padding:6}}>{(m.profit_factor || 0).toFixed(2)}</td></tr>
                        <tr><td style={{padding:6, color:'var(--text-secondary)'}}>Max drawdown</td><td style={{padding:6}}>{(m.max_drawdown_pct || 0).toFixed(2)}%</td></tr>
                      </tbody>
                    </table>
                  );

                  const chartOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, elements: { point: { radius: 0 } } };

                  return (
                    <div style={{display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap'}}>
                      {inR ? (
                        <div style={{flex:1, minWidth:280, background:'transparent', padding:8}}>
                          <div style={{fontSize:14, fontWeight:700, marginBottom:8}}>In-sample (70%)</div>
                          <div style={{height:120}}>
                            <Line data={{ labels: inR.equity_index, datasets:[{ label:'Equity', data: inR.equity_curve, borderColor:'#10b981', backgroundColor:'rgba(16,185,129,0.08)', fill:true }] }} options={chartOpts} />
                          </div>
                          <div style={{marginTop:8}}>{renderMetrics(inR.metrics)}</div>
                        </div>
                      ) : null}

                      {outR ? (
                        <div style={{flex:1, minWidth:280, background:'transparent', padding:8}}>
                          <div style={{fontSize:14, fontWeight:700, marginBottom:8}}>Out-of-sample (30%)</div>
                          <div style={{height:120}}>
                            <Line data={{ labels: outR.equity_index, datasets:[{ label:'Equity', data: outR.equity_curve, borderColor:'#3b82f6', backgroundColor:'rgba(59,130,246,0.06)', fill:true }] }} options={chartOpts} />
                          </div>
                          <div style={{marginTop:8}}>{renderMetrics(outR.metrics)}</div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()
              ) : (
                <div style={{color:'var(--text-secondary)'}}>No backtest results. Click "Run Backtest" to execute on current historical range.</div>
              )}
            </div>

            {/* Trading Strategies */}
            <div style={{marginTop:16, background:'var(--bg-tertiary)', padding:12, borderRadius:8, border:'1px solid var(--border-color)'}}>
              <div style={{fontSize:18, fontWeight:900, color:'#3b82f6', marginBottom:8}}>Trading Strategies</div>
              <div style={{display:'flex', gap:8, alignItems:'center', marginBottom:8}}>
                <button
                  onClick={() => runGoodTime(true, 5)}
                  disabled={loadingGoodTime}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid var(--border-color)',
                    background: loadingGoodTime ? 'var(--bg-tertiary)' : 'linear-gradient(90deg,#f97316,#fb7185)',
                    color: loadingGoodTime ? 'var(--text-secondary)' : 'white',
                    cursor: loadingGoodTime ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  {loadingGoodTime ? 'Checking...' : 'Find Trade Time'}
                </button>
                <button
                  onClick={() => setShowGoodTimePanel(s => !s)}
                  style={{padding:'6px 10px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-primary)'}}
                >
                  {showGoodTimePanel ? 'Hide' : 'Show Result'}
                </button>
              </div>

              {showGoodTimePanel && (
                <div style={{marginBottom:12, padding:12, background:'var(--bg-primary)', borderRadius:6, border:'1px solid var(--border-color)'}}>
                  {goodTimeResult ? (
                    goodTimeResult.error ? (
                      <div style={{color:'#ef4444'}}>Error: {goodTimeResult.error}</div>
                    ) : (
                      <div>
                        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                          <div style={{fontSize:16, fontWeight:800}}>Verdict:</div>
                          <div style={{fontSize:16, fontWeight:900, color: goodTimeResult.verdict === 'good' ? '#10b981' : '#ef4444'}}>{goodTimeResult.verdict?.toUpperCase()}</div>
                        </div>
                        {goodTimeResult.reasons && goodTimeResult.reasons.length ? (
                          <div style={{marginBottom:8}}>
                            <div style={{fontWeight:700, fontSize:13}}>Reasons:</div>
                            <ul style={{margin:6, paddingLeft:18}}>
                              {goodTimeResult.reasons.map((r,i)=> <li key={i} style={{color:'var(--text-secondary)'}}>{r}</li>)}
                            </ul>
                          </div>
                        ) : null}

                        {goodTimeResult.indicators && (
                          <div style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:8}}>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>Close: {goodTimeResult.indicators?.close ? fmtCurrency(goodTimeResult.indicators.close, currency) : '—'}</div>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>EMA200: {goodTimeResult.indicators?.ema200 ? fmtCurrency(goodTimeResult.indicators.ema200, currency) : '—'}</div>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>EMA50: {goodTimeResult.indicators?.ema50 ? fmtCurrency(goodTimeResult.indicators.ema50, currency) : '—'}</div>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>EMA20: {goodTimeResult.indicators?.ema20 ? fmtCurrency(goodTimeResult.indicators.ema20, currency) : '—'}</div>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>VOL20: {goodTimeResult.indicators?.vol20 ? fmtShortNumber(goodTimeResult.indicators.vol20) : '—'}</div>
                          </div>
                        )}

                        {goodTimeResult.proof && goodTimeResult.proof.samples && goodTimeResult.proof.samples.length ? (
                          <div style={{marginTop:8}}>
                            <div style={{fontWeight:700, marginBottom:6}}>Proof Samples (historical matches):</div>
                            <div style={{display:'flex', flexDirection:'column', gap:8}}>
                              {goodTimeResult.proof.samples.map((s, idx) => (
                                <div key={idx} style={{padding:8, background:'rgba(255,255,255,0.02)', borderRadius:6}}>
                                  <div style={{fontSize:12}}>Time: {s.time ? new Date(s.time).toLocaleString() : '—'}</div>
                                  <div style={{fontSize:12}}>Entry Price: {s.entry_price ? fmtCurrency(s.entry_price, currency) : '—'}</div>
                                  <div style={{fontSize:12}}>Return 3: {s.ret_3 !== null && s.ret_3 !== undefined ? ( (s.ret_3 * 100).toFixed(2) + '%' ) : '—'}</div>
                                  <div style={{fontSize:12}}>Return 10: {s.ret_10 !== null && s.ret_10 !== undefined ? ( (s.ret_10 * 100).toFixed(2) + '%' ) : '—'}</div>
                                  <div style={{fontSize:12}}>Return 30: {s.ret_30 !== null && s.ret_30 !== undefined ? ( (s.ret_30 * 100).toFixed(2) + '%' ) : '—'}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{color:'var(--text-secondary)'}}>No proof samples available.</div>
                        )}
                      </div>
                    )
                  ) : (
                    <div style={{color:'var(--text-secondary)'}}>No result yet. Click "Good Time to Buy" to evaluate.</div>
                  )}
                </div>
              )}
              
              {/* Strategy Explanations removed from here and relocated into the bottom Strategy Results area */}
              
              <div style={{marginTop:12, marginBottom:8}}>
                <button
                  onClick={() => setShowStrategyParams(!showStrategyParams)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                    background: showStrategyParams ? '#3b82f6' : 'var(--bg-secondary)',
                    color: showStrategyParams ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  {showStrategyParams ? 'Hide Parameters' : 'Adjust Parameters'}
                </button>
              </div>
              
              {showStrategyParams && (
                <div style={{marginBottom:12, padding:12, border:'1px solid var(--border-color)', borderRadius:4, background:'var(--bg-secondary)'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                    <div style={{fontSize:14, fontWeight:700}}>Strategy Parameters</div>
                    <button
                      onClick={() => setStrategyParams({
                        ema_pullback: {ema_short: 9, ema_long: 20, sma_trend: 50, stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        vwap_bounce: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        trend_day: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        orb: {or_hours: 2, stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        momentum_ignition: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        gap_go: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        vwap_mean_reversion: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        bollinger_reversion: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        rsi_fade: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        support_resistance: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        inside_bar: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        minute_momentum_scalp: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        spread_scalping: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        opening_drive: {stop_loss_pct: 0.01, take_profit_mult: 2.0},
                        pair_trading: {stop_loss_pct: 0.01, take_profit_mult: 2.0}
                      })}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: 11
                      }}
                    >
                      Reset to Defaults
                    </button>
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:12}}>
                    {Object.entries(strategyParams).map(([strategy, params]) => (
                      <div key={strategy} style={{border:'1px solid var(--border-color)', borderRadius:4, padding:8}}>
                        <div style={{fontWeight:700, marginBottom:6, textTransform:'capitalize'}}>
                          {strategy.replace('_', ' ')}
                        </div>
                        <div style={{display:'flex', flexDirection:'column', gap:4}}>
                          {Object.entries(params).map(([key, value]) => (
                            <div key={key} style={{display:'flex', alignItems:'center', gap:8}}>
                              <label style={{fontSize:11, minWidth:90, display:'flex', alignItems:'center', gap:6}}>
                                {t(`strategy.param.${key}`) || key.replace('_',' ')}:
                                <span title={t(`strategy.tooltip.${key}`) || ''} style={{cursor:'help', color:'var(--text-secondary)', fontSize:12}}>ℹ️</span>
                              </label>
                              {typeof value === 'string' ? (
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setStrategyParams(prev => ({ ...prev, [strategy]: { ...prev[strategy], [key]: newValue } }));
                                  }}
                                  style={{ flex:1, minWidth:0, width:'100%', boxSizing:'border-box', padding: '4px 6px', borderRadius:2, border:'1px solid var(--border-color)', background:'var(--bg-primary)', color:'var(--text-primary)', fontSize:12 }}
                                />
                              ) : (
                                <input
                                  type="number"
                                  value={value}
                                  onChange={(e) => {
                                    const newValue = parseFloat(e.target.value) || 0;
                                    setStrategyParams(prev => ({ ...prev, [strategy]: { ...prev[strategy], [key]: newValue } }));
                                  }}
                                  style={{ flex:1, minWidth:0, width:'100%', boxSizing:'border-box', padding: '4px 6px', borderRadius:2, border:'1px solid var(--border-color)', background:'var(--bg-primary)', color:'var(--text-primary)', fontSize:12 }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Trade Suggestion Button */}
              <div style={{marginBottom:12, padding:12, background:'var(--bg-secondary)', borderRadius:6, border:'1px solid var(--border-color)'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                  <div>
                    <div style={{fontSize:16, fontWeight:700, color:'#3b82f6'}}>Trade Suggestion</div>
                    <div style={{fontSize:12, color:'var(--text-secondary)'}}>Run all strategies and get color-coded recommendations</div>
                  </div>
                  <button
                    onClick={runTradeSuggestion}
                    disabled={tradeSuggestionLoading}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: '1px solid var(--border-color)',
                      background: tradeSuggestionLoading ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: tradeSuggestionLoading ? 'var(--text-secondary)' : 'white',
                      cursor: tradeSuggestionLoading ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {tradeSuggestionLoading ? (
                      <>
                        <LoadingSpinner size={16} color='var(--text-secondary)' />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        🎯 Run Analysis
                      </>
                    )}
                  </button>
                </div>
                <div style={{fontSize:12, color:'var(--text-secondary)'}}>
                  <strong>Color Guide:</strong> 
                  <span style={{color:'#10b981', marginLeft:8}}>🟢 Good</span>
                  <span style={{color:'#eab308', marginLeft:8}}>🟡 Neutral</span>
                  <span style={{color:'#ef4444', marginLeft:8}}>🔴 Bad</span>
                </div>
              </div>
              
              {/* Trade Suggestion Formulas removed from here and relocated into the bottom Strategy Results area */}
              
              {/* Trade Suggestion Parameters */}
              <div style={{marginBottom:12, padding:12, background:'var(--bg-secondary)', borderRadius:6, border:'1px solid var(--border-color)'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                  <div style={{fontSize:14, fontWeight:700}}>Trade Suggestion Parameters:</div>
                  <button
                    onClick={() => setShowTradeParameters(!showTradeParameters)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      border: '1px solid var(--border-color)',
                      background: showTradeParameters ? '#3b82f6' : 'var(--bg-primary)',
                      color: showTradeParameters ? 'white' : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 12
                    }}
                  >
                    {showTradeParameters ? 'Close' : 'Open'}
                  </button>
                </div>
                {showTradeParameters && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:12}}>
                    {[
                      {
                        name: 'Risk Management',
                        params: [
                          'Stop Loss: 1% default (configurable)',
                          'Take Profit: 2x stop loss default',
                          'Max Position Size: Portfolio risk based',
                          'Daily Loss Limit: 2% of portfolio'
                        ]
                      },
                      {
                        name: 'Entry Conditions',
                        params: [
                          'Volume Confirmation: Required for momentum strategies',
                          'Time Filters: Trading hours only (9:30-16:00 EST)',
                          'Liquidity Filters: Minimum volume thresholds',
                          'Gap Filters: Avoid extreme gap scenarios'
                        ]
                      },
                      {
                        name: 'Exit Conditions',
                        params: [
                          'Time-based exits: End of day closure',
                          'Profit targets: Dynamic based on volatility',
                          'Trailing stops: For trending strategies',
                          'Signal reversal: Opposite signal triggers'
                        ]
                      },
                      {
                        name: 'Performance Metrics',
                        params: [
                          'Win Rate: >50% target',
                          'Profit Factor: >1.5 target',
                          'Max Drawdown: <5% limit',
                          'Sharpe Ratio: Risk-adjusted returns'
                        ]
                      },
                      {
                        name: 'Market Conditions',
                        params: [
                          'Volatility filters: ATR-based thresholds',
                          'Trend strength: ADX >25 for trend strategies',
                          'Market regime: Bull/Bear/Sideways detection',
                          'News impact: Avoid high-impact news periods'
                        ]
                      },
                      {
                        name: 'Backtesting Parameters',
                        params: [
                          'Lookback Period: 1-2 years minimum',
                          'Commission: $0.005 per share',
                          'Slippage: 0.1% of price',
                          'Data Frequency: 1-minute bars'
                        ]
                      }
                    ].map(section => (
                      <div key={section.name} style={{fontSize:12, lineHeight:1.4, padding:8, border:'1px solid var(--border-color)', borderRadius:4}}>
                        <div style={{fontWeight:700, color:'#3b82f6', marginBottom:6}}>{section.name}</div>
                        <ul style={{margin:0, paddingLeft:16}}>
                          {section.params.map((param, idx) => (
                            <li key={idx} style={{color:'var(--text-secondary)', marginBottom:2}}>{param}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Group strategies by Term and Cap for clearer presentation */}
              <div style={{marginTop:8}}>
                <div style={{fontSize:14,fontWeight:700, marginBottom:8}}>Strategies by Term & Cap</div>
                {(() => {
                  const meta = STRATEGY_META;
                  const terms = Array.from(new Set(Object.values(meta).map(m => m.term))).filter(Boolean);
                  return terms.map(term => (
                    <div key={term} style={{marginBottom:12}}>
                      <div style={{fontWeight:700, marginBottom:6}}>{term}</div>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse'}}>
                          <thead>
                            <tr style={{borderBottom:'1px solid var(--border-color)'}}>
                              <th style={{textAlign:'left', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Strategy</th>
                              <th style={{textAlign:'center', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Large Cap</th>
                              <th style={{textAlign:'center', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Small Cap</th>
                              <th style={{textAlign:'right', padding:8, fontSize:12, color:'var(--text-secondary)'}}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(meta).filter(([,m]) => m.term === term).map(([key,m]) => (
                              <tr key={key} style={{borderBottom:'1px solid var(--border-color)'}}>
                                <td style={{padding:8}}>{m.title}</td>
                                <td style={{padding:8, textAlign:'center'}}>{renderCapIcon(m.cap.large)}</td>
                                <td style={{padding:8, textAlign:'center'}}>{renderCapIcon(m.cap.small)}</td>
                                <td style={{padding:8, textAlign:'right'}}>
                                  <button
                                    onClick={() => runStrategy(key, strategyParams[key] || {})}
                                    disabled={loadingStrategies[key]}
                                    style={{padding:'6px 10px', borderRadius:6, border:'1px solid var(--border-color)', background: loadingStrategies[key] ? 'var(--bg-tertiary)' : (strategiesData[key] ? '#10b981' : 'var(--bg-primary)'), color: loadingStrategies[key] ? 'var(--text-secondary)' : (strategiesData[key] ? 'white' : 'var(--text-primary)') }}
                                  >
                                    {loadingStrategies[key] ? 'Running...' : 'Run'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ));
                })()}
              </div>
              {Object.keys(strategiesData).length > 0 && (
                <div style={{marginTop:12}}>
                  {/* If user selected an active suggestion, render its panel first (use cached data if available) */}
                  {activeSuggestionStrategy && (
                    <div key={`active-${activeSuggestionStrategy}`} style={{marginBottom:12, padding:8, background:'var(--bg-secondary)', borderRadius:4}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom:4}}>
                        <div style={{fontWeight:700}}>{(TRADE_FORMULAS[activeSuggestionStrategy]?.title || activeSuggestionStrategy).toUpperCase()}</div>
                        <div style={{display:'flex', gap:8, alignItems:'center'}}>
                          <button onClick={() => setExpandedStrategies(s => ({...s, [activeSuggestionStrategy]: !s[activeSuggestionStrategy]}))} className="action-btn" style={{padding:'4px 8px'}}>{expandedStrategies[activeSuggestionStrategy] ? 'Hide' : 'Formula'}</button>
                          <button onClick={() => runStrategy(activeSuggestionStrategy, strategyParams[activeSuggestionStrategy] || {}, true, 3)} className="action-btn" style={{padding:'4px 8px', background:'linear-gradient(90deg,#06b6d4,#3b82f6)', color:'#fff'}}>Backtest</button>
                          <button onClick={() => setActiveSuggestionStrategy(null)} style={{background:'none', border:'none', color:'var(--text-secondary)', cursor:'pointer', fontSize:16}}>×</button>
                        </div>
                      </div>
                      <div>
                        <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                          <div>Expectancy: { (strategiesData[activeSuggestionStrategy]?.metrics?.expectancy ?? 0).toFixed(3) }</div>
                          <div>Win Rate: { strategiesData[activeSuggestionStrategy]?.metrics ? ((strategiesData[activeSuggestionStrategy].metrics.win_rate*100)||0).toFixed(1) + '%' : '—' }</div>
                          <div>Total Trades: {strategiesData[activeSuggestionStrategy]?.metrics?.total_trades ?? '—'}</div>
                          <div>Total PnL: ${strategiesData[activeSuggestionStrategy]?.metrics?.total_pnl?.toFixed(2) ?? '—'}</div>
                        </div>

                        {/* Compact Formula & Explanation shown in the result card */}
                        <div style={{marginTop:8, padding:8, background:'var(--bg-primary)', borderRadius:6}}>
                          <div style={{fontSize:13, fontWeight:700, marginBottom:6}}>Formula and explanation</div>
                          <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom:8}}>{strategiesData[activeSuggestionStrategy]?.proof?.explanation || TRADE_FORMULAS[activeSuggestionStrategy]?.formula || 'Formula not available for this strategy.'}</div>
                          <div style={{display:'flex', gap:8, alignItems:'center'}}>
                            <button onClick={() => runStrategy(activeSuggestionStrategy, strategyParams[activeSuggestionStrategy] || {}, true, 3)} className="action-btn" style={{padding:'6px 10px'}}>Run Backtest (proof)</button>
                            <div style={{fontSize:12, color:'var(--text-secondary)'}}>{strategiesData[activeSuggestionStrategy]?.proof?.trade_samples && strategiesData[activeSuggestionStrategy].proof.trade_samples.length ? '' : 'No proof samples available.'}</div>
                          </div>
                        </div>

                        {expandedStrategies[activeSuggestionStrategy] && (
                          <div style={{marginTop:8, padding:8, background:'var(--bg-primary)', borderRadius:6}}>
                            <div style={{fontSize:13, fontWeight:700, marginBottom:6}}>Proof samples</div>
                            {strategiesData[activeSuggestionStrategy]?.proof?.trade_samples && strategiesData[activeSuggestionStrategy].proof.trade_samples.length ? (
                              <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                {strategiesData[activeSuggestionStrategy].proof.trade_samples.map((samp, idx) => (
                                  <div key={idx} style={{padding:8, background:'rgba(255,255,255,0.02)', borderRadius:4}}>
                                    <div style={{fontSize:12, color:'var(--text-secondary)'}}>Entry: {samp.entry_time ? new Date(samp.entry_time).toLocaleString() : '—'} — {samp.entry_price ? fmtCurrency(samp.entry_price, currency) : '—'}</div>
                                    <div style={{fontSize:12, color:'var(--text-secondary)'}}>Exit: {samp.exit_time ? new Date(samp.exit_time).toLocaleString() : '—'} — {samp.exit_price ? fmtCurrency(samp.exit_price, currency) : '—'}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{color:'var(--text-secondary)'}}>No proof samples available.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {Object.entries(strategiesData).filter(([strategy]) => strategy !== activeSuggestionStrategy).map(([strategy, data]) => (
                    <div key={strategy} style={{marginBottom:12, padding:8, background:'var(--bg-secondary)', borderRadius:4}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom:4}}>
                        <div style={{fontWeight:700}}>{strategy.replace('_', ' ').toUpperCase()}</div>
                        <div style={{display:'flex', gap:8, alignItems:'center'}}>
                          <button onClick={() => { setExpandedStrategies(s => ({...s, [strategy]: !s[strategy]})); setActiveSuggestionStrategy(strategy); }} className="action-btn" style={{padding:'4px 8px'}}>{expandedStrategies[strategy] ? 'Hide' : 'Formula'}</button>
                          <button onClick={() => runStrategy(strategy, strategyParams[strategy] || {}, true, 3)} className="action-btn" style={{padding:'4px 8px', background:'linear-gradient(90deg,#06b6d4,#3b82f6)', color:'#fff'}}>Backtest</button>
                          <button
                            onClick={() => closeStrategy(strategy)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '2px 6px',
                              borderRadius: '50%'
                            }}
                            title="Close"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      {data.error ? (
                        <div style={{color:'red'}}>{data.error}</div>
                      ) : (
                        <div>
                          <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                            <div>Expectancy: {data.metrics?.expectancy?.toFixed(3)}</div>
                            <div>Win Rate: {(data.metrics?.win_rate * 100)?.toFixed(1)}%</div>
                            <div>Total Trades: {data.metrics?.total_trades}</div>
                            <div>Total PnL: ${data.metrics?.total_pnl?.toFixed(2)}</div>
                          </div>

                          {/* Compact Formula & Explanation shown in the result card */}
                          <div style={{marginTop:8, padding:8, background:'var(--bg-primary)', borderRadius:6}}>
                            <div style={{fontSize:13, fontWeight:700, marginBottom:6}}>Formula and explanation</div>
                            <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom:8}}>{data.proof?.explanation || (TRADE_FORMULAS && TRADE_FORMULAS[strategy] ? TRADE_FORMULAS[strategy].formula : null) || 'Formula not available for this strategy.'}</div>
                            <div style={{display:'flex', gap:8, alignItems:'center'}}>
                              <button onClick={() => runStrategy(strategy, strategyParams[strategy] || {}, true, 3)} className="action-btn" style={{padding:'6px 10px'}}>Run Backtest (proof)</button>
                              <div style={{fontSize:12, color:'var(--text-secondary)'}}>{data.proof?.trade_samples && data.proof.trade_samples.length ? '' : 'No proof samples available.'}</div>
                            </div>
                          </div>

                          {expandedStrategies[strategy] && (
                            <div style={{marginTop:8, padding:8, background:'var(--bg-primary)', borderRadius:6}}>
                              <div style={{fontSize:13, fontWeight:700, marginBottom:6}}>Proof samples</div>
                              {data.proof?.trade_samples && data.proof.trade_samples.length ? (
                                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                  {data.proof.trade_samples.map((samp, idx) => (
                                    <div key={idx} style={{padding:8, background:'rgba(255,255,255,0.02)', borderRadius:4}}>
                                      <div style={{fontSize:12, color:'var(--text-secondary)'}}>Entry: {samp.entry_time ? new Date(samp.entry_time).toLocaleString() : '—'} — {samp.entry_price ? fmtCurrency(samp.entry_price, currency) : '—'}</div>
                                      <div style={{fontSize:12, color:'var(--text-secondary)'}}>Exit: {samp.exit_time ? new Date(samp.exit_time).toLocaleString() : '—'} — {samp.exit_price ? fmtCurrency(samp.exit_price, currency) : '—'}</div>
                                      {samp.entry_row && (
                                        <div style={{marginTop:6, fontSize:12}}>
                                          <div style={{fontWeight:700}}>Entry indicators</div>
                                          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                                            {Object.entries(samp.entry_row).map(([k,v]) => (
                                              <div key={k} style={{fontSize:11, color:'var(--text-secondary)'}}>{k}: {typeof v === 'number' ? (k === 'Volume' ? fmtShortNumber(v) : Number(v).toFixed(2)) : String(v)}</div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{color:'var(--text-secondary)'}}>No proof samples available.</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* News Section */}
          <div style={{marginTop:16, background:'var(--bg-tertiary)', padding:12, borderRadius:8, border:'1px solid var(--border-color)'}}>
            <Suspense fallback={<div style={{color:'var(--text-secondary)'}}>Loading news…</div>}>
              <NewsList symbol={symbol} predictions={predictions} />
            </Suspense>
          </div>

          {/* Scroll to Top Button */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'var(--accent-blue)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              zIndex: 1000
            }}
            title="Scroll to top"
          >
            ↑
          </button>

          <Suspense fallback={<div style={{color:'var(--text-secondary)'}}>Loading chat…</div>}>
            <ChatBox apiBase={API_BASE} symbol={symbol} initialOpen={false} />
          </Suspense>

        </section>
      </div>
    </div>
  );
}
